/**
 * CodeBlock & Evidence Components — HMVMReviewer Design System Phase 1
 * 
 * Display code snippets and evidence with syntax highlighting shell
 * 
 * Features:
 * - Code block with optional filename and line numbers
 * - Evidence container for finding evidence
 * - Copy to clipboard functionality
 * - Uses semantic theme tokens
 * - Square geometry (border-radius: 0)
 */

import React, { HTMLAttributes, useState } from 'react';
import { useLocale } from '../../contexts/LocaleContext';

export interface CodeBlockProps extends HTMLAttributes<HTMLDivElement> {
  code: string;
  language?: string;
  filename?: string;
  lineNumbers?: boolean;
  highlightLines?: number[];
  maxHeight?: string;
}

export function CodeBlock({
  code,
  language = 'text',
  filename,
  lineNumbers = false,
  highlightLines = [],
  maxHeight,
  className = '',
  ...props
}: CodeBlockProps) {
  const { t } = useLocale();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  const lines = code.split('\n');

  return (
    <div
      className={`
        bg-[var(--reviewer-code-bg)]
        border border-[var(--reviewer-code-border)]
        ${className}
      `}
      {...props}
    >
      {/* Header */}
      {(filename || language) && (
        <div className="flex items-center justify-between px-4 py-2 border-b border-[var(--reviewer-code-border)]">
          <div className="flex items-center gap-2">
            {filename && (
              <span className="text-[var(--reviewer-text)] text-[var(--reviewer-text-sm)] font-mono">
                {filename}
              </span>
            )}
            {language && (
              <span className="text-[var(--reviewer-text-secondary)] text-[var(--reviewer-text-xs)] font-mono uppercase">
                {language}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={handleCopy}
            className="
              px-2 py-1
              text-[var(--reviewer-text-secondary)]
              hover:text-[var(--reviewer-text)]
              text-[var(--reviewer-text-xs)]
              font-mono
              transition-colors
              focus:outline-none
              focus-visible:ring-2 focus-visible:ring-[var(--reviewer-focus-ring)]
            "
            aria-label={t('code.copyAria')}
          >
            {copied ? t('common.copied') : t('common.copy')}
          </button>
        </div>
      )}
      
      {/* Code Content */}
      <div
        className="overflow-x-auto"
        style={{ maxHeight: maxHeight || 'none' }}
      >
        <pre className="p-4 m-0">
          <code className="font-mono text-[var(--reviewer-text-sm)] text-[var(--reviewer-code-text)]">
            {lineNumbers ? (
              lines.map((line, index) => {
                const lineNumber = index + 1;
                const isHighlighted = highlightLines.includes(lineNumber);
                return (
                  <div
                    key={index}
                    className={`flex ${isHighlighted ? 'bg-[var(--reviewer-accent-soft)]' : ''}`}
                  >
                    <span className="inline-block w-12 text-right pr-4 text-[var(--reviewer-text-tertiary)] select-none">
                      {lineNumber}
                    </span>
                    <span>{line || '\n'}</span>
                  </div>
                );
              })
            ) : (
              code
            )}
          </code>
        </pre>
      </div>
    </div>
  );
}

/**
 * Evidence Component - Display evidence for findings
 */
export interface EvidenceProps extends HTMLAttributes<HTMLDivElement> {
  title?: string;
  type?: 'code' | 'text' | 'diff';
  children: React.ReactNode;
}

export function Evidence({
  title = 'Evidence',
  type = 'code',
  children,
  className = '',
  ...props
}: EvidenceProps) {
  return (
    <div
      className={`
        border border-[var(--reviewer-border)]
        ${className}
      `}
      {...props}
    >
      <div className="px-4 py-2 bg-[var(--reviewer-bg-tertiary)] border-b border-[var(--reviewer-border)]">
        <h4 className="text-[var(--reviewer-text)] text-[var(--reviewer-text-sm)] font-medium font-mono uppercase m-0">
          {title}
        </h4>
      </div>
      <div className="p-4">
        {children}
      </div>
    </div>
  );
}
